import { useCallback, useEffect, useState } from 'react';
import {
  ADMIN_SERVICE_PROVIDER_PAGE_SIZE,
  getAdminServiceProvidersReadModel,
  type AdminServiceProviderCursor,
  type AdminServiceProviderDirectoryItem,
  type AdminServiceProvidersReadModel,
} from '../services/adminServiceProvidersService';

export interface UseAdminServiceProvidersState {
  data: AdminServiceProvidersReadModel | null;
  providers: AdminServiceProviderDirectoryItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  return 'Nie udało się odczytać katalogu serwisów.';
};

export const useAdminServiceProviders = (): UseAdminServiceProvidersState => {
  const [data, setData] = useState<AdminServiceProvidersReadModel | null>(null);
  const [providers, setProviders] = useState<AdminServiceProviderDirectoryItem[]>([]);
  const [cursor, setCursor] = useState<AdminServiceProviderCursor>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const readModel = await getAdminServiceProvidersReadModel(ADMIN_SERVICE_PROVIDER_PAGE_SIZE);
      setData(readModel);
      setProviders(readModel.providers);
      setCursor(readModel.cursor);
    } catch (readError) {
      console.error('[useAdminServiceProviders] Directory read failed:', readError);
      setError(getErrorMessage(readError));
      setData(null);
      setProviders([]);
      setCursor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!data?.hasMore || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const readModel = await getAdminServiceProvidersReadModel(
        ADMIN_SERVICE_PROVIDER_PAGE_SIZE,
        cursor
      );
      setData((current) =>
        current
          ? {
              ...readModel,
              metrics: readModel.metrics,
              providers: [...providers, ...readModel.providers],
            }
          : readModel
      );
      setProviders((current) => [...current, ...readModel.providers]);
      setCursor(readModel.cursor);
    } catch (readError) {
      console.error('[useAdminServiceProviders] Directory load more failed:', readError);
      setError(getErrorMessage(readError));
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, data?.hasMore, loadingMore, providers]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    data,
    providers,
    loading,
    loadingMore,
    error,
    refresh,
    loadMore,
  };
};
